import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Button,
  Collapse,
  Flex,
  HStack,
  Icon,
  IconButton,
  Link as ChakraLink,
  Text,
  Tooltip,
  VStack,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { Plural, Trans } from '@lingui/react/macro';
import { plural, t } from '@lingui/core/macro';
import { ChevronDown, ChevronRight, Download, FileX, RefreshCw } from 'lucide-react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { ConfirmationDialog } from '@duke-hq/ui/components/DialogBox/ConfirmationDialog';
import { ListCard } from '@duke-hq/ui/components/Cards/ListCard';
import { trpcAdmin, type RouterOutputs } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { getLanguage } from '@duke-hq/shared-constants/courseTranslationLanguages';
import {
  deriveTranslationStatus,
  type ContentTranslationUiStatus,
} from '@duke-hq/shared-constants/contentTranslationStatus';
import { iconSize, pxToRem } from '@duke-hq/ui/constants/scalable';
import { escapeCsvValue } from '../../lib/csv';
import {
  StatusLedDot,
  TranslationStatusChip,
} from '../../components/translations/TranslationStatusChip';
import {
  RetranslateCellDialog,
  RetranslateScopeDialog,
} from '../../components/translations/RetranslateDialogs';
import {
  CoverageBar,
  coverageBarSegments,
  type CoverageBarCounts,
} from '../../components/translations/CoverageBar';
import {
  buildLanguageRollups,
  type FailedCell,
  type LanguageRollup,
} from '../../components/translations/languageRollups';
import {
  aggregateCounts,
  courseHasPendingTranslation,
  coverageColorScheme,
  coverageLabel,
  TRANSLATION_POLL_INTERVAL_MS,
  type CoverageStatus,
} from '../../components/translations/coverageStatus';
import { withPermission } from '../../withPermission';
import { EditorLayout } from '../../navbar/EditorLayout';
import { EditorBreadcrumbs } from '../../navbar/EditorBreadcrumbs';
import {
  buildExceptionGroups,
  hasExceptions,
  totalCells,
  type ExceptionCell,
  type ExceptionGroupKey,
  type ExceptionRow,
} from './exceptions';
import { testIds } from './testIds';

type CourseTranslations = RouterOutputs['contentTranslations']['listByCourse'];
type TranslationUnit = CourseTranslations['units'][number];
type TranslationResource = TranslationUnit['resources'][number];

// At most one row per (resource, language): unique on (sourceKind, sourceId, language).
function rowForLanguage(resource: TranslationResource, languageCode: string) {
  return resource.rows.find((row) => row.language === languageCode);
}

// Null when the resource is unpublished (it can't be translated, so it's excluded).
function resourceStatusForLanguage(
  resource: TranslationResource,
  languageCode: string,
): ContentTranslationUiStatus | null {
  if (resource.currentPublishedVersionId === null) {
    return null;
  }
  return deriveTranslationStatus(
    rowForLanguage(resource, languageCode),
    resource.currentPublishedVersionId,
  );
}

// -- Summary verdict --------------------------------------------------------

// Through the shared ladder so the row reads like every other surface.
function rollupStatus(rollup: LanguageRollup): CoverageStatus {
  return aggregateCounts({
    ready: rollup.available,
    inProgress: rollup.pending,
    stale: rollup.stale,
    failed: rollup.failed,
    notTranslated: rollup.notTranslated,
    total: rollup.total,
    withGaps: rollup.withGaps,
  });
}

// The shared colour scheme at a `.600` text-contrast weight, so the verdict
// label's hue matches the rest of the UI.
function verdictTextColor(status: CoverageStatus): string {
  const scheme = coverageColorScheme(status);
  return scheme === 'gray' ? 'fg.muted' : `${scheme}.600`;
}

// -- CSV ---------------------------------------------------------------------

function buildCoverageCsv(units: TranslationUnit[], supportedLanguages: string[]): string {
  const languages = supportedLanguages.map((code) => getLanguage(code)).filter((l) => l != null);
  const header = ['Unit', 'Resource', ...languages.map((language) => language.englishName)];
  const lines: string[] = [header.map(escapeCsvValue).join(',')];

  for (const unit of units) {
    for (const resource of unit.resources) {
      const cells = languages.map((language) => {
        const status = resourceStatusForLanguage(resource, language.code);
        if (status === null) {
          return t`Not applicable`;
        }
        const label = csvStatusLabel(status);
        if (status !== 'available-partial') {
          return label;
        }
        const row = rowForLanguage(resource, language.code);
        const untranslated = row?.untranslatedSegmentCount;
        const total = row?.totalSegmentCount;
        return untranslated != null && total != null
          ? `${label} (${t`${untranslated} of ${total} segments in English`})`
          : label;
      });
      lines.push(
        [
          escapeCsvValue(unit.unitTitle ?? t`Unassigned`),
          escapeCsvValue(resource.title),
          ...cells.map(escapeCsvValue),
        ].join(','),
      );
    }
  }

  return lines.join('\r\n');
}

// Two resources can share a title (a conversation and its companion page), so
// rows carry the kind to tell them apart.
function kindLabel(kind: 'PAGE' | 'CONVERSATION'): string {
  return kind === 'CONVERSATION' ? t`Conversation` : t`Page`;
}

function csvStatusLabel(status: ContentTranslationUiStatus): string {
  switch (status) {
    case 'available':
      return t`Translated`;
    case 'available-partial':
      return t`Partly translated`;
    case 'queued':
    case 'generating':
      return t`Translating`;
    case 'stale':
      return t`Needs review`;
    case 'failed':
      return t`Failed`;
    case 'not-translated':
      return t`Not translated`;
    case 'cancelled':
      return t`Cancelled`;
  }
}

function downloadCoverageCsv(
  units: TranslationUnit[],
  supportedLanguages: string[],
  filename: string,
) {
  const csv = buildCoverageCsv(units, supportedLanguages);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = filename;
  link.click();

  URL.revokeObjectURL(url);
}

// -- Unit groups --------------------------------------------------------------

type GroupResource = {
  resource: TranslationResource;
  status: ContentTranslationUiStatus | null;
};

type GroupUnit = {
  unitOfCompetencyId: string | null;
  unitTitle: string | null;
  resources: GroupResource[];
};

// `stale` is readable but not current, so it doesn't count toward "done".
function isReady(status: ContentTranslationUiStatus | null): boolean {
  return status === 'available' || status === 'available-partial';
}

// The unit's at-a-glance state for its header, via the same attention ladder as
// the per-language verdict (failure > stale > in-flight > gaps > resting).
type UnitSummary = {
  total: number;
  ready: number;
  failed: number;
  withGaps: number;
  worst: ContentTranslationUiStatus | null;
};

function summariseUnit(unit: GroupUnit): UnitSummary {
  const statuses = unit.resources
    .map((entry) => entry.status)
    .filter((status): status is ContentTranslationUiStatus => status !== null);
  const ready = statuses.filter(isReady).length;
  const failed = statuses.filter((status) => status === 'failed').length;
  const withGaps = statuses.filter((status) => status === 'available-partial').length;

  let worst: ContentTranslationUiStatus | null;
  if (statuses.length === 0) {
    worst = null;
  } else if (failed > 0) {
    worst = 'failed';
  } else if (statuses.includes('stale')) {
    worst = 'stale';
  } else if (statuses.includes('generating')) {
    worst = 'generating';
  } else if (statuses.includes('queued')) {
    worst = 'queued';
  } else if (ready === statuses.length) {
    worst = withGaps > 0 ? 'available-partial' : 'available';
  } else {
    worst = 'not-translated';
  }

  return { total: statuses.length, ready, failed, withGaps, worst };
}

// The header speaks the same lexicon as every other surface: LED dot for state,
// tabular count for progress, gap/failure note riding along when non-zero.
function UnitProgressIndicator({ summary }: { summary: UnitSummary }) {
  const suffix =
    summary.failed > 0 ? (
      <Trans>{summary.failed} failed</Trans>
    ) : summary.withGaps > 0 ? (
      <Trans>{summary.withGaps} partly in English</Trans>
    ) : null;

  return (
    <HStack spacing={1.5} flexShrink={0}>
      {summary.worst !== null ? <StatusLedDot status={summary.worst} /> : null}
      <Text
        fontSize="xs"
        color={summary.failed > 0 ? 'red.600' : 'fg.default'}
        sx={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {summary.ready}/{summary.total}
        {suffix !== null ? <> · {suffix}</> : null}
      </Text>
    </HStack>
  );
}

function UnitGroup({
  unit,
  languageCode,
  onRetry,
  onRetranslate,
}: {
  unit: GroupUnit;
  languageCode: string;
  onRetry: (cell: FailedCell) => void;
  onRetranslate: (cell: FailedCell, segmentCount: number | null) => void;
}) {
  const summary = summariseUnit(unit);
  // Units needing a look (failures, gaps, stale) open ready to read; clean ones
  // start collapsed so a healthy course scans as a short list of headers.
  const needsALook = summary.failed > 0 || summary.withGaps > 0 || summary.worst === 'stale';
  const { isOpen, onToggle } = useDisclosure({ defaultIsOpen: needsALook });

  return (
    <Box data-testid={testIds.unitGroup}>
      <Flex
        as="button"
        type="button"
        onClick={onToggle}
        align="center"
        gap={2}
        w="full"
        py={2}
        px={1}
        borderRadius="md"
        textAlign="left"
        _hover={{ bg: 'bg.subtle' }}
      >
        <Icon as={isOpen ? ChevronDown : ChevronRight} boxSize={iconSize.sm} color="fg.muted" />
        <Text fontSize="sm" fontWeight="semibold" flex="1" noOfLines={1}>
          {unit.unitTitle ?? <Trans>Unassigned</Trans>}
        </Text>
        <UnitProgressIndicator summary={summary} />
      </Flex>
      {isOpen ? (
        <VStack align="stretch" spacing={0} mt={1} pl={7}>
          {unit.resources.map((entry, index) => (
            <ResourceRow
              key={entry.resource.resourceId}
              entry={entry}
              languageCode={languageCode}
              onRetry={onRetry}
              onRetranslate={onRetranslate}
              isFirst={index === 0}
            />
          ))}
        </VStack>
      ) : null}
    </Box>
  );
}

function ResourceRow({
  entry,
  languageCode,
  onRetry,
  onRetranslate,
  isFirst,
}: {
  entry: GroupResource;
  languageCode: string;
  onRetry: (cell: FailedCell) => void;
  onRetranslate: (cell: FailedCell, segmentCount: number | null) => void;
  isFirst: boolean;
}) {
  const { resource, status } = entry;
  const row = rowForLanguage(resource, languageCode);
  const canRetranslate =
    (status === 'available' || status === 'available-partial' || status === 'stale') &&
    resource.currentPublishedVersionId !== null;
  const untranslated = row?.untranslatedSegmentCount ?? null;
  const total = row?.totalSegmentCount ?? null;
  const showGap = status === 'available-partial' && untranslated != null && total != null;

  return (
    <Flex
      align="center"
      gap={3}
      py={2}
      borderTopWidth={isFirst ? undefined : '1px'}
      borderColor="border.default"
      data-testid={testIds.resourceRow}
    >
      <HStack flex="1" minW={0} spacing={1}>
        <Text fontSize="sm" noOfLines={1}>
          {resource.title}
        </Text>
        <Text fontSize="xs" color="fg.muted" flexShrink={0}>
          · {kindLabel(resource.resourceKind)}
        </Text>
      </HStack>
      {status === null ? (
        <Text fontSize="xs" color="fg.muted" flexShrink={0}>
          <Trans>Not published</Trans>
        </Text>
      ) : (
        <VStack align="end" spacing={0.5} flexShrink={0}>
          <TranslationStatusChip status={status} generationDetails={row} />
          {showGap ? (
            <Text fontSize="xs" color="fg.muted" data-testid={testIds.resourceGap}>
              <Trans>
                {untranslated} of {total} segments in English
              </Trans>
            </Text>
          ) : null}
        </VStack>
      )}
      {status === 'failed' && resource.currentPublishedVersionId !== null ? (
        <Tooltip label={t`Retry`}>
          <IconButton
            aria-label={t`Retry`}
            icon={<Icon as={RefreshCw} boxSize={iconSize.sm} />}
            size="xs"
            variant="ghost"
            color="fg.muted"
            flexShrink={0}
            onClick={() =>
              onRetry({
                resourceVersionId: Number(resource.currentPublishedVersionId),
                resourceKind: resource.resourceKind,
                language: languageCode,
              })
            }
            data-testid={testIds.retryButton}
          />
        </Tooltip>
      ) : null}
      {canRetranslate ? (
        <Tooltip label={t`Re-translate with the current engine`}>
          <IconButton
            aria-label={t`Re-translate with the current engine`}
            icon={<Icon as={RefreshCw} boxSize={iconSize.sm} />}
            size="xs"
            variant="ghost"
            color="fg.muted"
            flexShrink={0}
            onClick={() =>
              onRetranslate(
                {
                  resourceVersionId: Number(resource.currentPublishedVersionId),
                  resourceKind: resource.resourceKind,
                  language: languageCode,
                },
                row?.totalSegmentCount ?? null,
              )
            }
            data-testid={testIds.retranslateButton}
          />
        </Tooltip>
      ) : null}
    </Flex>
  );
}

// -- Language rows --------------------------------------------------------------

function LanguageRow({
  rollup,
  units,
  onRetry,
  onRetranslate,
}: {
  rollup: LanguageRollup;
  units: TranslationUnit[];
  onRetry: (cell: FailedCell) => void;
  onRetranslate: (cell: FailedCell, segmentCount: number | null) => void;
}) {
  const { isOpen, onToggle } = useDisclosure();
  const status = rollupStatus(rollup);
  const counts: CoverageBarCounts = {
    ready: rollup.available - rollup.withGaps,
    gaps: rollup.withGaps,
    inProgress: rollup.pending,
    attention: rollup.failed + rollup.stale,
    notStarted: rollup.notTranslated,
  };
  // The whole row is the hover target; the thin bar alone is too small to hit.
  const tooltip = `${rollup.englishName}: ${coverageBarSegments(counts).summary}`;

  const groupUnits = useMemo<GroupUnit[]>(() => {
    return units.map((unit) => ({
      unitOfCompetencyId: unit.unitOfCompetencyId,
      unitTitle: unit.unitTitle,
      resources: unit.resources.map((resource) => ({
        resource,
        status: resourceStatusForLanguage(resource, rollup.code),
      })),
    }));
  }, [units, rollup.code]);

  return (
    <Box borderBottomWidth="1px" borderColor="border.default" _last={{ borderBottomWidth: 0 }}>
      <Tooltip label={tooltip}>
        <Flex
          as="button"
          type="button"
          onClick={onToggle}
          align="center"
          gap={3}
          w="full"
          py={2}
          px={2}
          borderRadius="md"
          _hover={{ bg: 'bg.subtle' }}
          aria-expanded={isOpen}
          data-testid={testIds.languageRow}
        >
          <Icon
            as={isOpen ? ChevronDown : ChevronRight}
            boxSize={iconSize.sm}
            color="fg.muted"
            flexShrink={0}
          />
          <Box w={pxToRem(140)} flexShrink={0} textAlign="left">
            <Text
              fontSize="sm"
              fontWeight="medium"
              lang={rollup.code}
              dir={rollup.rtl ? 'rtl' : 'ltr'}
              textAlign="left"
              sx={{ unicodeBidi: 'isolate' }}
            >
              {rollup.endonym}
            </Text>
            <Text fontSize="xs" color="fg.muted" textAlign="left">
              {rollup.englishName}
            </Text>
          </Box>
          {/* Fixed-width bar, table logic: sized columns keep the name, bar and
              counts adjacent for one left-to-right scan, equal bars stay
              comparable across rows, and spare width falls at the row's end. */}
          <Box w={pxToRem(240)} flexShrink={0}>
            <CoverageBar counts={counts} />
          </Box>
          <Text
            fontSize="xs"
            color="fg.muted"
            flexShrink={0}
            w={pxToRem(64)}
            textAlign="right"
            sx={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {rollup.available}/{rollup.total}
          </Text>
          <Text
            fontSize="xs"
            fontWeight="medium"
            color={verdictTextColor(status)}
            flexShrink={0}
            textAlign="left"
          >
            {coverageLabel(status)}
          </Text>
          <Box flex="1" />
        </Flex>
      </Tooltip>
      <Collapse in={isOpen} animateOpacity>
        <VStack align="stretch" spacing={4} pl={8} pr={2} pb={4} pt={1}>
          {groupUnits.map((unit) => (
            <UnitGroup
              key={unit.unitOfCompetencyId ?? 'none'}
              unit={unit}
              languageCode={rollup.code}
              onRetry={onRetry}
              onRetranslate={onRetranslate}
            />
          ))}
        </VStack>
      </Collapse>
    </Box>
  );
}

// -- Needs attention worklist -------------------------------------------------

const GROUP_META: {
  key: ExceptionGroupKey;
  chipStatus: ContentTranslationUiStatus;
}[] = [
  { key: 'failed', chipStatus: 'failed' },
  { key: 'stale', chipStatus: 'stale' },
  { key: 'inFlight', chipStatus: 'generating' },
  { key: 'gaps', chipStatus: 'available-partial' },
];

// A worklist group shows this many rows before demanding a click, so one bad
// publish doesn't wall the ledger below off the screen.
const GROUP_ROW_CAP = 8;

// The neutral pill (NotOfferedChip's recipe) naming an affected language:
// endonym, an LED dot for in-flight and gap cells, and the gap ratio for
// partly-in-English cells. Full detail rides the tooltip.
function LanguageCellTag({
  cell,
  withDot,
  breathIndex,
}: {
  cell: ExceptionCell;
  withDot: boolean;
  breathIndex: number;
}) {
  const language = getLanguage(cell.language);
  const endonym = language?.endonym ?? cell.language;
  const english = language?.englishName ?? cell.language;
  const untranslated = cell.untranslatedSegmentCount;
  const total = cell.totalSegmentCount;
  const hasGapCounts = cell.status === 'available-partial' && untranslated != null && total != null;
  const tooltip =
    cell.status === 'available-partial' && untranslated != null && total != null
      ? t`${english} · ${untranslated} of ${total} segments in English`
      : `${english} · ${csvStatusLabel(cell.status)}`;

  return (
    <Tooltip label={tooltip}>
      <Badge
        variant="pill"
        fontSize="xs"
        fontWeight="medium"
        cursor="default"
        bg="gray.50"
        color="gray.600"
        boxShadow="inset 0 0 0px 1px var(--chakra-colors-gray-200)"
        data-testid={testIds.languageTag}
      >
        <HStack spacing="1.5" as="span">
          {withDot ? <StatusLedDot status={cell.status} breathIndex={breathIndex} /> : null}
          <Text
            as="span"
            lang={cell.language}
            dir={language?.rtl ? 'rtl' : 'ltr'}
            sx={{ unicodeBidi: 'isolate' }}
          >
            {endonym}
          </Text>
          {hasGapCounts ? (
            <Text as="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>
              · {untranslated}/{total}
            </Text>
          ) : null}
        </HStack>
      </Badge>
    </Tooltip>
  );
}

function ExceptionRowView({
  row,
  groupKey,
  isFirst,
  onRetryRow,
}: {
  row: ExceptionRow;
  groupKey: ExceptionGroupKey;
  isFirst: boolean;
  onRetryRow: (row: ExceptionRow) => void;
}) {
  // Only in-flight tags carry a dot (breathing = live signal); the group header
  // chip declares the state once, so repeating it per tag is noise.
  const withDot = groupKey === 'inFlight';

  // Each row highlights on hover so several read as a list. The negative margin
  // lets the hover fill bleed to the card edge while the padding keeps content
  // aligned with the group header above. `role="group"` lets the title react to
  // a row hover.
  // Tags and Retry sit beside the title; the trailing spacer eats wide-screen
  // slack at the row's end.
  return (
    <Flex
      role="group"
      align="center"
      gap={3}
      py={2}
      mx={-2}
      px={2}
      borderTopWidth={isFirst ? undefined : '1px'}
      borderColor="border.default"
      _hover={{ bg: 'bg.subtle' }}
      data-testid={testIds.attentionRow}
    >
      {/* Natural width: the title only truncates when the row genuinely runs out
          of room, never to hold a column boundary the row doesn't need. */}
      <HStack spacing={1} minW={0}>
        {/* Default ink so a list of titles stays quiet; a row hover reveals the
            underline, signalling the title links to the resource. The unit rides
            the tooltip; a dedicated unit column duplicated long module titles. */}
        <Tooltip label={row.unitTitle ?? undefined} openDelay={400}>
          <ChakraLink
            as={RouterLink}
            to={`/resources/${row.resourceId}`}
            fontSize="sm"
            color="fg.default"
            textDecoration="none"
            _groupHover={{ textDecoration: 'underline' }}
            _hover={{ textDecoration: 'underline' }}
            noOfLines={1}
          >
            {row.resourceTitle}
          </ChakraLink>
        </Tooltip>
        <Text fontSize="xs" color="fg.muted" flexShrink={0}>
          · {kindLabel(row.resourceKind)}
        </Text>
      </HStack>
      <HStack spacing={1.5} flexShrink={0} flexWrap="wrap">
        {row.cells.map((cell, index) => (
          <LanguageCellTag key={cell.language} cell={cell} withDot={withDot} breathIndex={index} />
        ))}
      </HStack>
      {/* Matches the conversation editor's inline-action idiom: a neutral ghost
          icon button (RefreshCw, no red) that fades in on row hover, beside the
          tags it acts on. Kept in layout at opacity 0 so revealing it never
          reflows the row; focus-visible reveals it for keyboard users. */}
      {groupKey === 'failed' ? (
        <Tooltip label={t`Retry`}>
          <IconButton
            aria-label={t`Retry`}
            icon={<Icon as={RefreshCw} boxSize={iconSize.sm} />}
            size="xs"
            variant="ghost"
            color="fg.muted"
            flexShrink={0}
            opacity={0}
            _groupHover={{ opacity: 1 }}
            _focusVisible={{ opacity: 1 }}
            transition="opacity 0.15s"
            onClick={() => onRetryRow(row)}
            data-testid={testIds.attentionRowRetry}
          />
        </Tooltip>
      ) : null}
      <Box flex="1" />
    </Flex>
  );
}

function ExceptionGroupView({
  meta,
  rows,
  trailing,
  onRetryRow,
}: {
  meta: (typeof GROUP_META)[number];
  rows: ExceptionRow[];
  trailing: ReactNode;
  onRetryRow: (row: ExceptionRow) => void;
}) {
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? rows : rows.slice(0, GROUP_ROW_CAP);
  const cellCount = totalCells(rows);

  return (
    <Box data-testid={testIds.attentionGroup} pt={4} _first={{ pt: 0 }}>
      {/* Action/counter sits with the chip and count, not at the card's far edge,
          which is a long way from its label on a wide screen. */}
      <Flex align="center" gap={3} pb={1}>
        <TranslationStatusChip status={meta.chipStatus} />
        <Text fontSize="xs" color="fg.muted">
          {meta.key === 'gaps' ? (
            <Plural
              value={rows.length}
              one="# resource partly in English"
              other="# resources partly in English"
            />
          ) : (
            <Plural value={cellCount} one="# translation" other="# translations" />
          )}
        </Text>
        {trailing}
      </Flex>
      {visible.map((row, index) => (
        <ExceptionRowView
          key={row.resourceId}
          row={row}
          groupKey={meta.key}
          isFirst={index === 0}
          onRetryRow={onRetryRow}
        />
      ))}
      {rows.length > GROUP_ROW_CAP && !showAll ? (
        <Button
          size="xs"
          variant="ghost"
          w="full"
          onClick={() => setShowAll(true)}
          data-testid={testIds.showAllButton}
        >
          <Trans>Show all {rows.length}</Trans>
        </Button>
      ) : null}
    </Box>
  );
}

// -- Page ------------------------------------------------------------------------

// The coverage page body, shared by the course and unit coverage pages. The data
// (units + the language set to measure against) and the surface-specific chrome
// (breadcrumbs, CSV filename) come from the caller; everything below — rollups,
// the needs-attention worklist, per-language drill-down and retry — is identical
// whichever level fed it.
export function TranslationCoverageView({
  scope,
  scopeId,
  data,
  isLoading,
  isError,
  onRefetch,
  breadcrumbs,
  csvFilename,
}: {
  // Which level fed this view, so the copy can name it ("...for this course").
  scope: 'course' | 'unit';
  // The course or unit id, for the scope-wide re-translate.
  scopeId: string;
  data: RouterOutputs['contentTranslations']['listByCourse'] | undefined;
  isLoading: boolean;
  isError: boolean;
  onRefetch: () => void;
  breadcrumbs: ReactNode;
  csvFilename: string;
}) {
  const toast = useToast();
  const [isRetrying, setIsRetrying] = useState(false);
  const confirmRegenerate = useDisclosure();
  const retry = trpcAdmin.contentTranslations.retry.useMutation();
  const refetch = onRefetch;

  // -- Forced re-translation ---------------------------------------------------

  const [retranslateCell, setRetranslateCell] = useState<{
    cell: FailedCell;
    segmentCount: number | null;
  } | null>(null);
  const confirmRetranslateScope = useDisclosure();
  const retranslateOne = trpcAdmin.contentTranslations.retranslate.useMutation();
  const retranslateCourse = trpcAdmin.contentTranslations.retranslateCourse.useMutation();
  const retranslateUnit = trpcAdmin.contentTranslations.retranslateUnit.useMutation();
  // Fetched only while the dialog is open, so opening it states the spend.
  const courseEstimate = trpcAdmin.contentTranslations.retranslateCourseEstimate.useQuery(
    { courseId: scopeId },
    { enabled: scope === 'course' && confirmRetranslateScope.isOpen },
  );
  const unitEstimate = trpcAdmin.contentTranslations.retranslateUnitEstimate.useQuery(
    { unitId: scopeId },
    { enabled: scope === 'unit' && confirmRetranslateScope.isOpen },
  );
  const scopeEstimate = scope === 'course' ? courseEstimate.data : unitEstimate.data;

  // Both handlers catch rather than let the rejection escape: a thrown mutation
  // would otherwise leave the dialog open with no feedback. The dialog stays
  // open on error so the confirm can be retried.
  const handleRetranslateCell = async () => {
    if (retranslateCell == null) {
      return;
    }
    try {
      const { enqueued } = await retranslateOne.mutateAsync(retranslateCell.cell);
      setRetranslateCell(null);
      void refetch();
      toast(
        enqueued
          ? {
              title: t`Re-translation started`,
              status: 'success',
              isClosable: true,
              duration: 3000,
            }
          : {
              title: t`Couldn't start this re-translation`,
              status: 'error',
              isClosable: true,
              duration: 5000,
            },
      );
    } catch {
      toast({
        title: t`Couldn't start this re-translation`,
        status: 'error',
        isClosable: true,
        duration: 5000,
      });
    }
  };

  const handleRetranslateScope = async () => {
    try {
      const { translationKickoff } =
        scope === 'course'
          ? await retranslateCourse.mutateAsync({ courseId: scopeId })
          : await retranslateUnit.mutateAsync({ unitId: scopeId });
      confirmRetranslateScope.onClose();
      void refetch();
      toast(
        translationKickoff.translationsEnqueued > 0
          ? {
              title: t`Re-translating ${translationKickoff.translationsEnqueued} translations`,
              status: 'success',
              isClosable: true,
              duration: 3000,
            }
          : {
              title: t`Nothing to re-translate`,
              status: 'info',
              isClosable: true,
              duration: 5000,
            },
      );
    } catch {
      toast({
        title: t`Couldn't start re-translation`,
        status: 'error',
        isClosable: true,
        duration: 5000,
      });
    }
  };

  const units = useMemo(() => data?.units ?? [], [data]);
  const supportedLanguages = useMemo(() => data?.supportedLanguages ?? [], [data]);

  // One walk over every published resource x supported language: builds the
  // per-language rollups (page rows) and collects the cells the bulk retry needs.
  const { rollups, failedCells } = useMemo(
    () => buildLanguageRollups(units, supportedLanguages),
    [units, supportedLanguages],
  );

  const hasAnyPublished = rollups.some((rollup) => rollup.total > 0);
  // Fully translated only: no missing resources AND no English gaps, so the
  // subtitle never rounds a partly-in-English language up to done.
  const fullyTranslatedLanguages = rollups.filter(
    (rollup) => rollup.total > 0 && rollup.available === rollup.total && rollup.withGaps === 0,
  ).length;

  const exceptionGroups = useMemo(
    () => buildExceptionGroups(units, supportedLanguages),
    [units, supportedLanguages],
  );
  const showWorklist = hasExceptions(exceptionGroups);

  // "5/8 done" on the Translating group: the peak in-flight count this visit is
  // the denominator, so the counter ticks up as polling lands cells. Resets when
  // everything lands (the group unmounts anyway).
  const inFlightCellCount = totalCells(exceptionGroups.inFlight);
  const peakInFlight = useRef(0);
  useEffect(() => {
    if (inFlightCellCount === 0) {
      peakInFlight.current = 0;
    } else if (inFlightCellCount > peakInFlight.current) {
      peakInFlight.current = inFlightCellCount;
    }
  }, [inFlightCellCount]);
  const inFlightDone = Math.max(0, peakInFlight.current - inFlightCellCount);

  const runRetries = async (cells: FailedCell[]) => {
    setIsRetrying(true);
    const results = await Promise.allSettled(
      cells.map((cell) =>
        retry.mutateAsync({
          resourceVersionId: cell.resourceVersionId,
          resourceKind: cell.resourceKind,
          language: cell.language,
        }),
      ),
    );
    setIsRetrying(false);

    // A fulfilled mutation can still be a no-op (`enqueued: false`), so count what
    // was actually queued rather than what merely didn't throw.
    const queued = results.filter(
      (result) => result.status === 'fulfilled' && result.value.enqueued,
    ).length;
    const couldNotQueue = results.length - queued;
    void refetch();

    if (queued === 0) {
      toast({
        title: t`Couldn't start ${couldNotQueue} translations`,
        status: 'error',
        isClosable: true,
        duration: 5000,
      });
      return;
    }

    toast({
      title:
        couldNotQueue === 0
          ? t`Retrying ${queued} translations`
          : t`Retrying ${queued} translations, ${couldNotQueue} couldn't be started`,
      status: couldNotQueue === 0 ? 'success' : 'warning',
      isClosable: true,
      duration: couldNotQueue === 0 ? 3000 : 5000,
    });
  };

  const handleRegenerateFailed = async () => {
    confirmRegenerate.onClose();
    await runRetries(failedCells);
  };

  const handleRetryOne = async (cell: FailedCell) => {
    try {
      const { enqueued } = await retry.mutateAsync(cell);
      void refetch();
      toast(
        enqueued
          ? { title: t`Retrying translation`, status: 'success', duration: 3000 }
          : { title: t`Couldn't start this translation`, status: 'warning', isClosable: true },
      );
    } catch {
      toast({ title: t`Couldn't retry translation`, status: 'error', isClosable: true });
    }
  };

  // Row-level retry: every failed language on the resource. Fine-grained
  // one-language retry lives in the ledger drill-down below.
  const handleRetryRow = (row: ExceptionRow) => {
    if (row.currentPublishedVersionId === null) {
      return;
    }
    const cells: FailedCell[] = row.cells.map((cell) => ({
      resourceVersionId: Number(row.currentPublishedVersionId),
      resourceKind: row.resourceKind,
      language: cell.language,
    }));
    if (cells.length === 1 && cells[0] !== undefined) {
      void handleRetryOne(cells[0]);
      return;
    }
    void runRetries(cells);
  };

  // Subtitle fragments, shown only when there are 2+ groups to summarise; with
  // one group the subtitle would just repeat that group's header.
  const attentionParts: string[] = [];
  const failedCellCount = totalCells(exceptionGroups.failed);
  const staleCellCount = totalCells(exceptionGroups.stale);
  if (failedCellCount > 0) {
    attentionParts.push(t`${failedCellCount} failed`);
  }
  if (staleCellCount > 0) {
    attentionParts.push(plural(staleCellCount, { one: '# needs review', other: '# need review' }));
  }
  if (inFlightCellCount > 0) {
    attentionParts.push(t`${inFlightCellCount} translating`);
  }
  if (exceptionGroups.gaps.length > 0) {
    attentionParts.push(
      plural(exceptionGroups.gaps.length, {
        one: '# resource partly in English',
        other: '# resources partly in English',
      }),
    );
  }
  const attentionSubtitle = attentionParts.length > 1 ? attentionParts.join(' · ') : undefined;

  return (
    <EditorLayout
      toolbar={{
        breadcrumbs,
        // No toolbar actions: bulk retry lives with the failed group in the
        // worklist, and CSV export sits with the coverage-by-language data it
        // exports. The toolbar keeps its height via SecondaryHeader's minH.
      }}
      body={{
        kind: 'content',
        children: (
          <Box data-testid={testIds.page}>
            {isError ? (
              <Alert status="error" borderRadius="md" data-testid={testIds.errorAlert}>
                <AlertIcon />
                <AlertDescription flex="1">
                  <Trans>Couldn't load translation coverage.</Trans>
                </AlertDescription>
                <Button size="sm" variant="outline" onClick={() => void refetch()}>
                  <Trans>Retry</Trans>
                </Button>
              </Alert>
            ) : isLoading || data == null ? null : (
              <>
                {/* Absent entirely on a healthy course: the calm state is just
                    the ledger below. */}
                {showWorklist ? (
                  <ListCard
                    cardTitle={t`Needs attention`}
                    cardSubtitle={attentionSubtitle}
                    mb={6}
                    data-testid={testIds.attentionCard}
                    cardContent={
                      <Box>
                        {GROUP_META.filter((meta) => exceptionGroups[meta.key].length > 0).map(
                          (meta) => (
                            <ExceptionGroupView
                              key={meta.key}
                              meta={meta}
                              rows={exceptionGroups[meta.key]}
                              trailing={
                                meta.key === 'failed' ? (
                                  <Button
                                    size="xs"
                                    variant="ghost"
                                    leftIcon={<Icon as={RefreshCw} boxSize={iconSize.sm} />}
                                    isLoading={isRetrying}
                                    onClick={confirmRegenerate.onOpen}
                                    data-testid={testIds.regenerateFailedButton}
                                  >
                                    <Trans>Retry all</Trans>
                                  </Button>
                                ) : meta.key === 'inFlight' && inFlightDone > 0 ? (
                                  <Text
                                    fontSize="xs"
                                    color="fg.muted"
                                    sx={{ fontVariantNumeric: 'tabular-nums' }}
                                  >
                                    <Trans>
                                      {inFlightDone}/{peakInFlight.current} done
                                    </Trans>
                                  </Text>
                                ) : null
                              }
                              onRetryRow={handleRetryRow}
                            />
                          ),
                        )}
                      </Box>
                    }
                  />
                ) : null}

                {/* The same card family as the course editor's Units of Competency
                    card. Always visible, never collapsed: "is Arabic done?" must
                    never cost a click. */}
                <ListCard
                  cardTitle={t`Coverage by language`}
                  cardSubtitle={
                    hasAnyPublished ? (
                      <Text as="span" data-testid={testIds.summaryLine}>
                        {scope === 'unit' ? (
                          <Trans>
                            {fullyTranslatedLanguages} of {rollups.length} languages fully
                            translated for this unit
                          </Trans>
                        ) : (
                          <Trans>
                            {fullyTranslatedLanguages} of {rollups.length} languages fully
                            translated for this course
                          </Trans>
                        )}
                      </Text>
                    ) : (
                      t`No published resources to translate yet.`
                    )
                  }
                  cardPrimaryAction={
                    hasAnyPublished ? (
                      <HStack spacing={1}>
                        <Button
                          size="sm"
                          variant="ghost"
                          leftIcon={<Icon as={Download} boxSize={iconSize.sm} />}
                          onClick={() =>
                            downloadCoverageCsv(units, supportedLanguages, csvFilename)
                          }
                          data-testid={testIds.downloadCsvButton}
                        >
                          <Trans>Export CSV</Trans>
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          leftIcon={<Icon as={RefreshCw} boxSize={iconSize.sm} />}
                          onClick={confirmRetranslateScope.onOpen}
                          data-testid={testIds.retranslateScopeButton}
                        >
                          <Trans>Re-translate</Trans>
                        </Button>
                      </HStack>
                    ) : null
                  }
                  cardContent={
                    hasAnyPublished ? (
                      <Box>
                        {rollups.map((rollup) => (
                          <LanguageRow
                            key={rollup.code}
                            rollup={rollup}
                            units={units}
                            onRetry={(cell) => void handleRetryOne(cell)}
                            onRetranslate={(cell, segmentCount) =>
                              setRetranslateCell({ cell, segmentCount })
                            }
                          />
                        ))}
                      </Box>
                    ) : (
                      <VStack
                        align="center"
                        spacing="2"
                        py="6"
                        px="4"
                        data-testid={testIds.emptyState}
                      >
                        <Icon as={FileX} boxSize="6" color="fg.muted" />
                        <Text fontSize="sm" fontWeight="medium">
                          <Trans>No published resources to translate yet</Trans>
                        </Text>
                        <Text fontSize="xs" color="fg.muted" textAlign="center">
                          <Trans>
                            Publish a page or conversation to start generating translations.
                          </Trans>
                        </Text>
                      </VStack>
                    )
                  }
                />
                <ConfirmationDialog
                  isOpen={confirmRegenerate.isOpen}
                  onClose={confirmRegenerate.onClose}
                  title={t`Retry failed translations?`}
                  body={t`This will retry ${failedCells.length} failed translations. They'll show as Translating while they regenerate.`}
                  confirmLabel={t`Retry`}
                  isLoading={isRetrying}
                  onConfirm={() => void handleRegenerateFailed()}
                />
                <RetranslateCellDialog
                  isOpen={retranslateCell != null}
                  onClose={() => setRetranslateCell(null)}
                  isLoading={retranslateOne.isPending}
                  languageName={
                    retranslateCell == null
                      ? ''
                      : (getLanguage(retranslateCell.cell.language)?.englishName ??
                        retranslateCell.cell.language)
                  }
                  segmentCount={retranslateCell?.segmentCount ?? null}
                  onConfirm={() => void handleRetranslateCell()}
                />
                <RetranslateScopeDialog
                  isOpen={confirmRetranslateScope.isOpen}
                  onClose={confirmRetranslateScope.onClose}
                  isLoading={retranslateCourse.isPending || retranslateUnit.isPending}
                  title={
                    scope === 'unit'
                      ? t`Re-translate this unit's content?`
                      : t`Re-translate this course's content?`
                  }
                  estimate={scopeEstimate}
                  onConfirm={() => void handleRetranslateScope()}
                />
              </>
            )}
          </Box>
        ),
      }}
    />
  );
}

export const CourseTranslationCoverage = withPermission('canManageCourseContent', () => {
  const { courseId } = useParams();
  if (courseId === null || courseId === undefined) {
    throw new Error('courseId is required');
  }

  const { data: course } = trpcAdmin.courses.byId.useQuery(courseId);
  const query = trpcAdmin.contentTranslations.listByCourse.useQuery(
    { courseId },
    {
      // Keep polling on a transient load error so it self-heals rather than
      // freezing on stale data until a manual refresh.
      refetchInterval: (query) =>
        courseHasPendingTranslation(query.state.data?.units) || query.state.status === 'error'
          ? TRANSLATION_POLL_INTERVAL_MS
          : false,
    },
  );

  const courseCrumbLabel = [course?.identifier, course?.title].filter(Boolean).join(' ');

  return (
    <TranslationCoverageView
      scope="course"
      scopeId={courseId}
      data={query.data}
      isLoading={query.isLoading}
      isError={query.isError}
      onRefetch={() => void query.refetch()}
      csvFilename={`course-${courseId}-translation-coverage.csv`}
      breadcrumbs={
        <EditorBreadcrumbs
          crumbs={[
            { label: t`Courses`, to: '/courses' },
            { label: courseCrumbLabel || t`Course`, to: `/courses/${courseId}` },
          ]}
          current={
            <Text fontSize="sm" fontWeight="medium" whiteSpace="nowrap">
              <Trans>Translations</Trans>
            </Text>
          }
        />
      }
    />
  );
});
